import { SVGS } from '@/assets/svgs'
import colors from '@/styles/colors'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import SVG from 'react-inlinesvg'
import { CBox } from '@/pages/__styled'
import ChangePasswordModal from './ChangePasswordModal'
import DeactivateAccountModal from './DeactivateAccountModal'
import DeleteAccountModal from './DeleteAccountModal'

interface Props {
  onSubmitDeactivateAccount: () => void
  onSubmitDeleteAccount: () => void
}

export default function AccountManagement({
  onSubmitDeactivateAccount,
  onSubmitDeleteAccount,
}: Props) {
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const theme = useTheme()
  const isLessThanMedium = useMediaQuery(theme.breakpoints.down('md'))

  const onClickDeactivate = () => setShowDeactivateModal(true)
  const onClickDelete = () => setShowDeleteModal(true)
  const onClickChangePassword = () => setShowChangePasswordModal(true)

  const cardsData = [
    {
      icon: SVGS.PasswordLockIcon,
      title: 'Change Password',
      description: 'Periodically change your password to protect account',
      buttonTitle: 'Change Password',
      tintColor: colors.meshdomBlue,
      onClick: onClickChangePassword,
    },
    {
      icon: SVGS.DeactivateAccountIcon,
      title: 'Deactivate Account',
      description: 'You can activate your account when you want.',
      buttonTitle: 'Deactivate My Account',
      tintColor: '#EE28FF',
      onClick: onClickDeactivate,
    },
    {
      icon: SVGS.DeleteAccountIcon,
      title: 'Delete Account',
      description: 'You can not recover your account anymore.',
      buttonTitle: 'Delete My Account',
      tintColor: '#FF2828',
      onClick: onClickDelete,
    },
  ]

  return (
    <Box
      sx={{
        width: '100%',
        background: '#ffffff',
        borderRadius: '16px',
        padding: '28px',
        boxShadow:
          '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(226, 232, 240, 0.8)',
        border: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow:
            '0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(99, 102, 241, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#0f172a',
          letterSpacing: '-0.02em',
          mb: 3,
        }}
      >
        Account Settings
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLessThanMedium ? 'column' : 'row',
          gap: '20px',
          width: '100%',
        }}
      >
        {cardsData.map((e, i) => (
          <CBox
            key={`${i}`}
            sx={{
              flex: 1,
              minWidth: isLessThanMedium ? '100%' : '220px',
              backgroundColor: '#f8fafc',
              px: '24px',
              py: '28px',
              borderRadius: '14px',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#ffffff',
                borderColor: `${e.tintColor}40`,
                boxShadow: `0 4px 16px ${e.tintColor}20`,
                transform: 'translateY(-4px)',
              },
            }}
          >
            <Box
              sx={{
                width: '72px',
                height: '72px',
                borderRadius: '16px',
                backgroundColor: `${e.tintColor}10`,
                border: `1px solid ${e.tintColor}25`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <SVG src={e.icon} width={'36px'} height={'36px'} color={e.tintColor} />
            </Box>
            <CBox gap={'10px'}>
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 700,
                  textAlign: 'center',
                  color: '#0f172a',
                  letterSpacing: '-0.01em',
                }}
              >
                {e.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  textAlign: 'center',
                  color: '#64748b',
                  lineHeight: 1.6,
                }}
              >
                {e.description}
              </Typography>
            </CBox>
            <Button
              onClick={e.onClick}
              sx={{
                color: e.tintColor,
                backgroundColor: `${e.tintColor}10`,
                border: `2px solid ${e.tintColor}30`,
                fontWeight: 600,
                fontSize: '14px',
                letterSpacing: '-0.01em',
                padding: '10px 20px',
                borderRadius: '10px',
                textTransform: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: e.tintColor,
                  color: '#ffffff',
                  borderColor: e.tintColor,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${e.tintColor}40`,
                },
                '&:active': {
                  transform: 'translateY(0) scale(0.95)',
                },
              }}
            >
              {e.buttonTitle}
            </Button>
          </CBox>
        ))}
      </Box>
      <DeactivateAccountModal
        open={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={onSubmitDeactivateAccount}
      />

      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={onSubmitDeleteAccount}
      />

      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </Box>
  )
}
